import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import TestIds from '../../../testIds'
import Input from '../../../components/Input'
import { useSelector } from 'react-redux'
import {
  selectCurrentFarmerAccount,
  selectEmailForPrefill,
  selectNameForPrefill,
  selectPhoneForPrefill,
} from '../../azure-b2c/storage'
import { isInvalidEmail } from '../../../util/validateEmail'
import { isInvalidPhoneNumber } from '../../../util/validatePhoneNumber'
import { isInvalidName } from '../../../util/validateName'
import { validateReference } from '../../../util/validateReference'
import { colors } from '../../../themes/colors'

export enum ContactComponents {
  EMAIL = 'email',
  NAME = 'name',
  PHONE = 'phone',
  REFERENCE = 'reference',
}

interface UseContactInfoProps {
  components: ContactComponents[]
  isTfo: boolean
}

export const useContactInfo = ({ isTfo, ...props }: UseContactInfoProps) => {
  const accountEmail = useSelector(selectEmailForPrefill)
  const accountUsername = useSelector(selectNameForPrefill)
  const accountPhone = useSelector(selectPhoneForPrefill)
  const account = useSelector(selectCurrentFarmerAccount)

  const [name, setName] = useState<string | undefined>(accountUsername ?? '')
  const [email, setEmail] = useState<string | undefined>(accountEmail ?? '')
  const [phone, setPhone] = useState<string | undefined>(accountPhone ?? '')
  const [reference, setReference] = useState<string | undefined>('')

  const [emailError, setEmailError] = useState<string>()
  const [phoneError, setPhoneError] = useState<string>()
  const [nameError, setNameError] = useState<string>()
  const [refError, setRefError] = useState<string>()

  useEffect(() => {
    setEmail(accountEmail ?? '')
    setName(accountUsername ?? '')
    setPhone(accountPhone ?? '')
  }, [accountEmail, accountUsername, accountPhone])

  const validateContactInfo = () => {
    const components = props.components
    const _emailError = components.includes(ContactComponents.EMAIL)
      ? isInvalidEmail(email)
      : undefined
    setEmailError(_emailError)

    const _phoneError = components.includes(ContactComponents.PHONE)
      ? isInvalidPhoneNumber(phone)
      : undefined
    setPhoneError(_phoneError)

    const _nameError = components.includes(ContactComponents.NAME) ? isInvalidName(name) : undefined
    setNameError(_nameError)

    const _refError =
      components.includes(ContactComponents.REFERENCE) && account?.captureCustomerReference
        ? validateReference(reference)
        : undefined
    setRefError(_refError)

    if (_emailError || _phoneError || _nameError || _refError) {
      return false
    }

    return true
  }

  return {
    email,
    setEmail,
    name,
    setName,
    phone,
    setPhone,
    reference,
    setReference,
    refError,
    emailError,
    setEmailError,
    phoneError,
    setPhoneError,
    nameError,
    setNameError,
    validateContactInfo,

    ContactInfoComponent: (
      <ContactInfo
        isTfo={isTfo}
        name={name}
        setName={setName}
        nameError={nameError}
        email={email}
        setEmail={setEmail}
        emailError={emailError}
        phone={phone}
        setPhone={setPhone}
        phoneError={phoneError}
        reference={reference}
        setReference={setReference}
        refError={refError}
        referenceRequired={!!account?.captureCustomerReference}
        components={props.components}
      />
    ),
  }
}

interface ContactInfoProps {
  isTfo: boolean
  name: string | undefined
  setName: React.Dispatch<React.SetStateAction<string | undefined>>
  nameError: string | undefined
  email: string | undefined
  setEmail: React.Dispatch<React.SetStateAction<string | undefined>>
  emailError: string | undefined
  phone: string | undefined
  setPhone: React.Dispatch<React.SetStateAction<string | undefined>>
  phoneError: string | undefined
  reference: string | undefined
  setReference: React.Dispatch<React.SetStateAction<string | undefined>>
  refError: string | undefined
  referenceRequired: boolean
  components: ContactComponents[]
}

export const ContactInfo: React.FC<ContactInfoProps> = ({
  isTfo,
  email,
  setEmail,
  name,
  setName,
  phone,
  setPhone,
  reference,
  setReference,
  refError,
  referenceRequired,
  emailError,
  phoneError,
  nameError,
  components,
}) => {
  const map = {
    [ContactComponents.NAME]: (
      <Input
        key='name'
        label={isTfo ? 'Customer Name' : 'Name'}
        required
        value={name}
        setValue={setName}
        errorMessage={nameError}
        type='fname'
        testId={TestIds.components.contactInfo.nameInput}
      />
    ),

    [ContactComponents.EMAIL]: (
      <Input
        key='email'
        label={isTfo ? 'Customer Email address' : 'Email address'}
        required
        value={email}
        setValue={setEmail}
        errorMessage={emailError}
        type='email'
        testId={TestIds.components.contactInfo.emailInput}
      />
    ),

    [ContactComponents.PHONE]: (
      <Input
        key='phone'
        label={isTfo ? 'Customer Phone number' : 'Phone number'}
        required
        value={phone}
        setValue={setPhone}
        errorMessage={phoneError}
        type='tel'
        inputBaseProps={{ pattern: '[^\\s]*', type: 'tel' }}
        testId={TestIds.components.contactInfo.phoneInput}
      />
    ),

    [ContactComponents.REFERENCE]: (
      <Input
        key='reference'
        label={isTfo ? 'Customer Reference' : 'Your reference'}
        value={reference}
        setValue={setReference}
        errorMessage={refError}
        required={referenceRequired}
        inputBaseProps={{
          maxLength: 60,
        }}
        type='text'
        testId={TestIds.components.contactInfo.refInput}
        helperText={`${reference?.length ?? 0}/60 characters`}
        helperTextColor={colors.grey700}
      />
    ),
  }
  return (
    <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0, width: '100%' }}>
      {components.map((e) => map[e])}
    </Box>
  )
}
